/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  Avatar,
  Table,
  Input,
  Spin,
  Switch,
  Select,
  Tooltip,
  Alert,
  TreeSelect,
  FloatButton,
} from 'antd'
import {
  paragraphSmall,
  weightRegular,
  weightSemiBold,
} from '../../components/css'
import {
  SearchOutlined,
  QuestionCircleOutlined,
  ArrowUpOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons'
import OptionButtonIcon from '../../components/List/OptionButtons/Icon'
import OptionButtonDelete from '../../components/List/OptionButtons/Delete'
import MoreOptions, {
  MoreOptionListItem,
} from '../../components/List/OptionButtons/More'
import useScheduleList from './useScheduleList'
import colors from '@sportninja/common/constants/appColors'
import Icon from '../../components/Icon'
import useWindowSize from '../../hooks/useWindowSize'
import { useSports } from '@sportninja/common/hooks/useSports'
import { getAllGenericSportProperties } from '@sportninja/common/selectors/types'
import { connect } from 'react-redux'
import { useDeleteModal } from '../../hooks/useDeleteModal'
import req from '@sportninja/common/api/request'
import DeleteCompetitionModal from '../../components/Modal/DeleteCompetitionModal'
import RegistrationStatusTag from '../../components/RegistrationStatusTag'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import React, { Suspense } from 'react'

const FlightsModal = React.lazy(() => import('../../components/FlightsModal'))
import { paragraphSmallStyle } from '../RegistrationInviteV2/steps/Main'
import {
  transformToTreeSelectData,
  findNodeById,
  treeNodeToOrganization,
  getFirstLevelKeys,
} from '../MySchedule/utils/treeSelectUtils'
import { getTableStyles } from '../../styles/tableStyles'
import Text from '../../components/ts-components/Text'
import MembershipTypeFilter from '../MySchedule/components/MembershipTypeFilter'
import MembershipTypeInfoLabel from '../MySchedule/components/MembershipTypeFilter/MembershipTypeInfoLabel'
import {
  COMPETITION_TYPE_FILTER_OPTIONS,
  COMPETITION_TYPE_LABELS,
} from '@sportninja/common/features/competition/constants'

dayjs.extend(utc)

const spinnerTinyStyle = { width: 18 }
const iconClickStyle = { fontSize: 16, cursor: 'pointer' }
const dropdownScrollStyle = { maxHeight: 400, overflow: 'auto' }
const suffixIconStyle = { color: colors.NEUTRAL_50, cursor: 'pointer' }

const SCROLL_THRESHOLD = 500
const ALL_VALUE = 'all'

const ORG_PLACEHOLDER =
  '/images/generic_placeholders/placeholder_organization.png'

const filterTreeSelect = css`
  ${paragraphSmall}
  ${weightRegular}
  width: 200px;
  height: 40px !important;
  color: ${colors.WHITE};

  .ant-select-selector {
    background: transparent !important;
    border-radius: 8px !important;
    height: 40px !important;
  }

  .ant-select-selection-search-input {
    height: 38px !important;
    color: ${colors.WHITE} !important;
  }

  .ant-select-selection-item {
    font-size: 14px !important;
    line-height: 38px !important;
    color: ${colors.WHITE} !important;
  }

  .ant-select-selection-placeholder {
    line-height: 38px !important;
    font-size: 14px !important;
    color: ${colors.NEUTRAL_50} !important;
  }

  .ant-select-clear {
    color: ${colors.WHITE};
  }

  @media (max-width: 1023px) {
    width: 100%;
  }
`

const treeNodeStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  min-height: 36px;
`

const treeNodeImageStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid ${colors.NEUTRAL_50};
  align-self: center;
`

const treeNodeTextStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 1;
`

const treeNodeNameStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.WHITE};
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const filterFieldWrapperStyle = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-self: flex-start;
  }
`

const filterSelectStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  width: 200px;
  height: 40px;
  .ant-select-selector {
    background-color: transparent !important;
    color: ${colors.WHITE} !important;
    border-radius: 8px !important;
    height: 40px !important;
    font-size: 14px !important;
  }
  .ant-select-arrow {
    color: ${colors.NEUTRAL_100};
  }
  .ant-select-clear {
    background: transparent;
    color: ${colors.NEUTRAL_100};
  }
  @media (max-width: 1023px) {
    width: 100%;
  }
`

const OptionButtonArchive = ({
  Button,
  entityType,
  itemId,
  isArchived,
  onComplete,
  onToggle,
  name,
}) => {
  const actionType = isArchived ? 'unarchive' : 'archive'
  const iconName = isArchived ? 'box-open' : 'archive'
  const title = isArchived ? `Unarchive ${name}` : `Archive ${name}`
  const buttonText = isArchived
    ? 'Unarchive Competition'
    : 'Archive Competition'
  const archiveModal = useDeleteModal()

  const onClick = async () => {
    await req(`/${entityType}/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify({
        is_archive: !isArchived,
      }),
    })
    await onComplete()
  }

  const handleClick = () => {
    archiveModal.open()
    if (onToggle) {
      onToggle()
    }
  }

  return (
    <>
      {Button ? (
        <Button onClick={handleClick} />
      ) : (
        <MoreOptionListItem
          onClick={handleClick}
          iconName={iconName}
          title={buttonText}
        />
      )}
      <DeleteCompetitionModal
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={onClick}
        title={title}
        message={`Are You Sure You Want To ${actionType} This Competition?`}
        secondaryMessage={
          isArchived
            ? 'This Will Make The Competition Visible In The Main List Again.'
            : 'This Will Hide The Competition From The Main List.'
        }
        itemName={name}
        buttonText={isArchived ? 'Unarchive' : 'Archive'}
        displayBanner={true}
        bannerText={`${name} ${actionType}d successfully`}
      />
    </>
  )
}

const CustomOptionButtonFavourite = ({
  entityType,
  item,
  isFavourite,
  isFavoritesView,
  onFavoriteChange,
}) => {
  const [loading, setLoading] = useState(false)
  const [isFav, setIsFav] = useState(isFavourite)

  useEffect(() => {
    setIsFav(isFavourite)
  }, [isFavourite])

  const entityName = useCallback(() => {
    switch (entityType) {
      case ENTITY_TYPES.org:
        return ' organization'
      case ENTITY_TYPES.team:
        return ' team'
      case ENTITY_TYPES.schedule:
        return ' competition'
      case ENTITY_TYPES.game:
        return ' game'
      default:
        return ''
    }
  }, [entityType])

  const onClick = async () => {
    setLoading(true)
    try {
      if (isFavoritesView) {
        const newState = !isFav
        setIsFav(newState)
        onFavoriteChange(newState)
      } else {
        await req(`/favorites/${entityType}/${item.id}`, {
          method: isFav ? 'DELETE' : 'POST',
        })
        setIsFav(!isFav)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
      `}
    >
      {loading ? (
        <div style={spinnerTinyStyle}>
          <Spin size='small' style={{ color: colors.PRIMARY }} />
        </div>
      ) : (
        <div
          css={css`
            position: relative;
            display: inline-block;
          `}
        >
          <Tooltip
            title={`${isFav ? 'Unfollow' : 'Follow'}${entityName()}`}
            mouseEnterDelay={1}
            placement='bottom'
          >
            <OptionButtonIcon
              faType={isFav ? 'fas' : 'far'}
              name='star'
              onClick={onClick}
            />
          </Tooltip>
        </div>
      )}
    </div>
  )
}

const renderCloneCompetitionButton = ({ onClick, onToggle }) => {
  return (
    <MoreOptionListItem
      onClick={() => {
        onClick()
        onToggle()
      }}
      iconName='clone'
      faType='far'
      title={`Clone ${t('common:competition')}`}
    />
  )
}

const renderGeneratePluginTag = ({ onClick }) => {
  return (
    <MoreOptionListItem
      onClick={() => {
        onClick()
      }}
      iconName='plug'
      faType='far'
      title={'Generate JS Plugin Tag'}
    />
  )
}

// eslint-disable-next-line no-unused-vars
const MoreOptionsDivider = () => {
  return (
    <div
      css={css`
        height: 1px;
        background-color: ${colors.SOFT_STEEL};
      `}
    />
  )
}

const submenuItemStyle = css`
  padding: 12px 20px 12px 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background-color 0.15s ease-in-out;
  white-space: nowrap;
  background-color: ${colors.SECONDARY_400};
  &:hover {
    background-color: ${colors.SOFT_STEEL};
    p,
    i,
    a {
      color: white;
    }
  }
  p,
  i,
  a {
    transition: color 0.15s ease-in-out;
    color: white;
  }
`

const newBadgeStyle = css`
  background-color: ${colors.PRIMARY};
  color: ${colors.NEUTRAL_900};
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
  text-transform: uppercase;
  line-height: 1.2;
`

// eslint-disable-next-line no-unused-vars
const ScheduleSubmenu = ({ scheduleId, scheduleName, onToggle }) => {
  const [submenuOpen, setSubmenuOpen] = useState(false)
  const [flightsModalOpen, setFlightsModalOpen] = useState(false)

  return (
    <div>
      <div
        onClick={() => setSubmenuOpen((prev) => !prev)}
        css={css`
          background-color: ${submenuOpen ? colors.SOFT_STEEL : colors.HEADER};
          padding: 12px 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: background-color 0.15s ease-in-out;
          &:hover {
            background-color: ${colors.SOFT_STEEL};
            p,
            i {
              color: white;
            }
          }
          p,
          i {
            transition: color 0.15s ease-in-out;
            color: white;
          }
        `}
      >
        <OptionButtonIcon
          name='calendar-alt'
          css={css`
            margin-right: 8px;
          `}
        />
        <p
          css={css`
            flex: 1;
          `}
        >
          Scheduler Wizard
        </p>
        <span css={newBadgeStyle}>New</span>
        <RightOutlined
          css={css`
            font-size: 10px;
            color: ${colors.NEUTRAL_50};
            margin-left: 8px;
            transition: transform 0.15s ease-in-out;
            transform: rotate(${submenuOpen ? '90deg' : '0deg'});
          `}
        />
      </div>

      {submenuOpen && (
        <div>
          <Link
            to={`/schedule/${scheduleId}?view=admin`}
            onClick={() => onToggle?.()}
            css={css`
              text-decoration: none;
              color: inherit;
              &:hover {
                color: inherit;
              }
            `}
          >
            <div css={submenuItemStyle}>
              <OptionButtonIcon
                name='magic'
                css={css`
                  margin-right: 8px;
                `}
              />
              <p>New Schedule</p>
            </div>
          </Link>
          <div
            css={submenuItemStyle}
            onClick={() => {
              setFlightsModalOpen(true)
              onToggle?.()
            }}
          >
            <OptionButtonIcon
              name='list-alt'
              css={css`
                margin-right: 8px;
              `}
            />
            <p>View Flights</p>
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        <FlightsModal
          isOpen={flightsModalOpen}
          onClose={() => setFlightsModalOpen(false)}
          scheduleId={scheduleId}
          scheduleName={scheduleName || ''}
        />
      </Suspense>
    </div>
  )
}

const ScheduleListTable = ({
  refreshKey,
  setCount = () => {},
  slug,
  genericSports,
  account,
  onClickSandboxTour = () => {},
  onEditSchedule = () => {},
  setInitialState = () => {},
  setOnTriggerRefresh = () => {},
  onClickWebPluginOnScheduleList = () => {},
  isParentOrgProfessionalOrUltimateAccount = false,
  setSyncSalesforce = () => {},
  setScheduleInitialStateId = () => {},
  onClickRegistrationSetup = () => {},
  setRegistrationScheduleId = () => {},
  /** Opens the Create Competition wizard with the row's schedule pre-selected. */
  onCloneCompetition = () => {},
  alwaysListArchived = false,
  orgAdminPermission = false,
  isFavorites = false,
  showParentColumn,
  parentId,
  orgFeatures,
  isMembership = false,
  initialMembershipType = 'both',
  shouldShowUsersMeFilters = false,
  specificNameColumn = 'name',
  onMembershipTypeChange = null,
  queryRecursive = false,
  displayOrganizationColumn = false,
  showTotalCount = false,
  externalMembershipType,
  onMembershipTypeChangeExternal,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])
  const { sports, fetchSports } = useSports()

  const {
    items,
    loading,
    hasMore,
    totalCount,
    permissions,
    search,
    sportFilter,
    competitionTypeFilter,
    showArchived,
    membershipType,
    handleSearch,
    handleSort,
    handleSportFilter,
    handleCompetitionTypeFilter,
    handleShowArchived,
    handleMembershipType,
    handleOrganizationIdChange,
    loadMore,
    refresh,
    sort,
    direction,
    updateSportFilterList,
    error,
  } = useScheduleList(slug, {
    alwaysListArchived,
    initialMembershipType,
    queryRecursive,
    externalMembershipType,
    onMembershipTypeChangeExternal,
  })

  // Local state for managing favorites when isFavorites is true
  const [localFavorites, setLocalFavorites] = useState(new Set())

  // Organizations filter state (only used when shouldShowUsersMeFilters is true)
  const [organizationsTree, setOrganizationsTree] = useState(null)
  const [organizationsLoading, setOrganizationsLoading] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const [organizationsDropdownOpen, setOrganizationsDropdownOpen] =
    useState(false)

  // Ref to prevent multiple simultaneous loadMore calls
  const isLoadingMoreRef = useRef(false)

  // Ref to track initial mount and prevent refresh on first render
  const isInitialMountRef = useRef(true)

  useEffect(() => {
    if (!sports || sports?.length === 0) {
      fetchSports()
    }
  }, [fetchSports, sports])

  // Fetch organizations tree for filter (only when shouldShowUsersMeFilters is true)
  const fetchOrganizationsTree = useCallback(async () => {
    if (!shouldShowUsersMeFilters) {
      return
    }

    setOrganizationsLoading(true)
    try {
      const query = { membership_type: membershipType }
      const { data } = await req('/users/me2/organizations/tree', { query })
      setOrganizationsTree(data || [])
    } catch (err) {
      console.error('Error fetching organizations tree:', err)
      setOrganizationsTree([])
    } finally {
      setOrganizationsLoading(false)
    }
  }, [shouldShowUsersMeFilters, membershipType])

  // Initial fetch of organizations tree
  useEffect(() => {
    if (shouldShowUsersMeFilters) {
      fetchOrganizationsTree()
    }
  }, [shouldShowUsersMeFilters, fetchOrganizationsTree])

  // Transform organizations tree data for TreeSelect
  const organizationsTreeData = useMemo(() => {
    if (!organizationsTree) {
      return []
    }

    const allOption = {
      value: ALL_VALUE,
      title: isCanlan ? 'All Leagues' : 'All Organizations',
      label: isCanlan ? 'All Leagues' : 'All Organizations',
      isLeaf: true,
    }

    return [allOption, ...transformToTreeSelectData(organizationsTree)]
  }, [organizationsTree])

  // Default expanded keys (first level)
  const defaultExpandedKeys = useMemo(() => {
    if (!organizationsTree) {
      return []
    }
    return getFirstLevelKeys(organizationsTree)
  }, [organizationsTree])

  // Custom tree node renderer with image
  const renderTreeNode = useCallback((nodeData) => {
    // "All" option doesn't have data - but still needs consistent padding
    if (!nodeData.data) {
      return (
        <div css={treeNodeStyle}>
          <span css={treeNodeNameStyle}>{nodeData.title}</span>
        </div>
      )
    }

    const handleImageError = (e) => {
      e.currentTarget.onerror = null
      e.currentTarget.src = ORG_PLACEHOLDER
    }

    return (
      <div css={treeNodeStyle}>
        <img
          src={nodeData.data.image_url || ORG_PLACEHOLDER}
          alt={nodeData.data.name}
          css={treeNodeImageStyle}
          onError={handleImageError}
          loading='lazy'
        />
        <div css={treeNodeTextStyle}>
          <span css={treeNodeNameStyle}>{nodeData.title}</span>
        </div>
      </div>
    )
  }, [])

  const { groupedSportOptions, flatSportOptions } = useMemo(() => {
    const fullStatsOptions =
      sports
        ?.map((sport) => ({
          label: sport.name_full,
          value: sport.id,
          text: sport.name_full,
          is_generic: false,
        }))
        .filter((value) => value?.label !== 'Generic')
        .sort((a, b) => a.label.localeCompare(b.label)) || []

    const genericSportId = sports?.find((s) => s.name_full === 'Generic')?.id
    const basicStatsOptions = []

    if (genericSports) {
      genericSports.forEach((sport) => {
        if (!basicStatsOptions.find((s) => s.value === sport.id)) {
          basicStatsOptions.push({
            label: `${sport.name} (Basic)`,
            value: sport.id,
            text: `${sport.name} (Basic)`,
            is_generic: true,
            generic_sport_id: genericSportId,
          })
        }
      })
    }

    basicStatsOptions.sort((a, b) => a.label.localeCompare(b.label))

    const grouped = [
      {
        label: 'All Sports',
        value: ALL_VALUE,
      },
      {
        label: (
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <span
              css={css`
                ${paragraphSmallStyle}
                ${weightSemiBold}
                color: ${colors.PRIMARY};
                text-transform: uppercase;
              `}
            >
              Full Stats
            </span>
            <Tooltip title="What's the difference between Full Stats vs Basic Stats?">
              <QuestionCircleOutlined
                style={iconClickStyle}
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(
                    'https://support.sportninja.com/scoring-2-versions-basic-vs-full-stats',
                    '_blank'
                  )
                }}
              />
            </Tooltip>
          </div>
        ),
        options: fullStatsOptions,
      },
      {
        label: (
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <span
              css={css`
                ${paragraphSmallStyle}
                ${weightSemiBold}
                color: ${colors.PRIMARY};
                text-transform: uppercase;
              `}
            >
              Basic Stats
            </span>
            <Tooltip title="What's the difference between Full Stats vs Basic Stats?">
              <QuestionCircleOutlined
                style={iconClickStyle}
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(
                    'https://support.sportninja.com/scoring-2-versions-basic-vs-full-stats',
                    '_blank'
                  )
                }}
              />
            </Tooltip>
          </div>
        ),
        options: basicStatsOptions,
      },
    ]

    return {
      groupedSportOptions: grouped,
      flatSportOptions: [...fullStatsOptions, ...basicStatsOptions],
    }
  }, [sports, genericSports])

  // This function is used to filter the options in the select dropdown
  // when we group the options
  const filterOptionGroup = useCallback((s, optionOrGroup) => {
    const isGroup = Array.isArray(optionOrGroup.options)
    if (isGroup) {
      return false
    }
    return optionOrGroup.label.toLowerCase().includes(s.toLowerCase())
  }, [])

  useEffect(() => {
    updateSportFilterList(flatSportOptions)
  }, [flatSportOptions, updateSportFilterList])

  useEffect(() => {
    setOnTriggerRefresh(() => refresh)
  }, [refresh, setOnTriggerRefresh])

  // Refresh when refreshKey changes
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }

    if (refreshKey !== undefined) {
      refresh()
    }
    // `refresh` intentionally excluded — its identity changes whenever filter
    // state changes, which would re-trigger this effect and double-fire the
    // request. We only want to react to the parent bumping refreshKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey])

  // Initialize local favorites when items change and we're in favorites view
  useEffect(() => {
    if (isFavorites && items) {
      const favoriteIds = new Set(items.map((item) => item.id))
      setLocalFavorites(favoriteIds)
    }
  }, [items, isFavorites])

  const handleFavoriteChange = useCallback(
    (itemId, newFavoriteState) => {
      if (isFavorites) {
        setLocalFavorites((prev) => {
          const newSet = new Set(prev)
          if (newFavoriteState) {
            newSet.add(itemId)
          } else {
            newSet.delete(itemId)
          }
          return newSet
        })
      }
    },
    [isFavorites]
  )

  const handleScroll = useCallback(() => {
    if (loading || isLoadingMoreRef.current) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore) {
      isLoadingMoreRef.current = true
      loadMore()
    }
  }, [hasMore, loading, loadMore])

  useEffect(() => {
    if (!loading) {
      isLoadingMoreRef.current = false
    }
  }, [loading])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // Check if user has admin permission
  const hasAdminPermission = useMemo(() => {
    return Object.values(permissions).some((permission) => permission?.admin)
  }, [permissions])

  const columns = useMemo(() => {
    const cols = [
      {
        title: t('common:name'),
        key: specificNameColumn,
        dataIndex: specificNameColumn,
        sorter: true,
        width: isMobile ? 150 : 400,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder:
          sort === specificNameColumn
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) => (
          <Link
            to={{
              pathname: `${ROUTES.SCHEDULE_ROOT}/${item.id}`,
              state: { fromParent: parentId },
            }}
            onClick={async () => {
              await onClickSandboxTour()
            }}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              width: 100%;
              max-width: 400px;
              overflow: hidden;
              color: ${colors.NEUTRAL_0};
              &:hover {
                color: ${colors.PRIMARY};
              }
            `}
          >
            <Avatar
              src={
                getImageThumbnailId(item) ||
                item?.image?.url ||
                '/images/generic_placeholders/placeholder_competition.png'
              }
              icon={
                <Icon
                  name={item.is_tournament ? 'trophy' : 'calendar-alt'}
                  fontSize={14}
                />
              }
              shape='square'
              css={css`
                background-color: white;
                flex-shrink: 0;
                width: 32px;
                height: 32px;
                border-radius: 8px !important;
              `}
            />
            <div
              css={css`
                flex: 1;
                min-width: 0;
                overflow: hidden;
                display: flex;
                flex-direction: column;
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_0}
              >
                {item?.name || item?.name_full}
              </Text>
              {(item?.roles?.length >= 1 || item?.is_archive) && (
                <Text
                  variant='paragraphXSmall'
                  color={colors.NEUTRAL_50}
                  weight='regular'
                  style={`
                      text-transform: capitalize;
                    `}
                >
                  {item?.roles?.length >= 1 && (
                    <span>
                      {Array.from(
                        new Set(item?.roles?.map((role) => role.role_name))
                      ).join(' | ')}
                    </span>
                  )}
                  {item?.is_archive && (
                    <span>
                      {item?.roles?.length >= 1 && ' | '}
                      <span
                        css={css`
                          color: ${colors.PRIMARY};
                        `}
                      >
                        Archived
                      </span>
                    </span>
                  )}
                </Text>
              )}
            </div>
          </Link>
        ),
      },
    ]

    if (displayOrganizationColumn) {
      cols.push({
        title: isCanlan ? 'League' : 'Organization',
        key: 'organization.name',
        dataIndex: 'organization.name',
        sorter: false,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder: false,
        width: isMobile ? 100 : 230,
        render: (_, item) =>
          item?.organization?.id ? (
            <Link
              to={{
                pathname: `${ROUTES.ORG_ROOT}/${item?.organization?.id}`,
                state: { fromParent: parentId },
              }}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;
                color: ${colors.NEUTRAL_0};
              `}
            >
              {/* <Avatar
                src={
                  getImageThumbnailId(item?.organization) ||
                  item?.organization?.image?.url ||
                  '/images/generic_placeholders/placeholder_organization.png'
                }
                icon={<Icon name='building' fontSize={14} />}
                shape='square'
                css={css`
                  background-color: ${colors.NEUTRAL_0};
                  flex-shrink: 0;
                  width: 32px;
                  height: 32px;
                  border-radius: 8px !important;
                `}
              /> */}
              <div
                css={css`
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                  display: flex;
                  flex-direction: column;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                  style={`
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  `}
                >
                  {item?.organization?.name}
                </Text>
              </div>
            </Link>
          ) : null,
      })
    }

    if (showParentColumn) {
      cols.push({
        title: t('common:parentOrg'),
        key: 'organization.name_full',
        dataIndex: ['organization', 'name_full'],
        sorter: false,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder: null,
        render: (_, item) => (
          <Link to={`${ROUTES.ORG_ROOT}/${item?.organization?.id}`}>
            {item?.organization?.name_full}
          </Link>
        ),
      })
    }

    cols.push(
      {
        title: 'Sport',
        key: 'sport_id',
        dataIndex: 'sport_id',
        sorter: false,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder: null,
        render: (_, item) => (
          <span>
            {item?.generic_sport_name
              ? `${item?.generic_sport_name} (Basic)`
              : sports?.find((s) => s.id === item?.sport_id)?.name_full}
          </span>
        ),
        width: isMobile ? 80 : 150,
      },
      {
        title: t('common:type'),
        key: 'competition_type',
        dataIndex: 'competition_type',
        sorter: false,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder: null,
        render: (_, item) => (
          <span>
            {COMPETITION_TYPE_LABELS[item?.competition_type] ||
              item?.competition_type ||
              ''}
          </span>
        ),
        width: isMobile ? 60 : 80,
      },
      {
        title: 'Games',
        key: 'games_count',
        dataIndex: 'games_count',
        sorter: false,
        showSorterTooltip: false,
        sortOrder: null,
        width: isMobile ? 50 : 60,
        align: 'right',
      },
      {
        width: isMobile ? 80 : 150,
        title: t('common:start'),
        key: 'starts_at',
        dataIndex: 'starts_at',
        sorter: true,
        showSorterTooltip: false,
        sortOrder:
          sort === 'starts_at'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) => (
          <span>{dayjs.utc(item.starts_at).format('MMM DD, YYYY')}</span>
        ),
      },
      {
        width: isMobile ? 80 : 150,
        title: t('common:end'),
        key: 'ends_at',
        dataIndex: 'ends_at',
        sorter: true,
        showSorterTooltip: false,
        sortOrder:
          sort === 'ends_at'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) => (
          <span>{dayjs.utc(item.ends_at).format('MMM DD, YYYY')}</span>
        ),
      }
    )

    const hasAtLeastOneRegistrationEnabled =
      items?.some((item) => item.is_registration_on) || false
    if (hasAtLeastOneRegistrationEnabled) {
      cols.push({
        title: 'Reg. Status',
        key: 'reg_status',
        sorter: false,
        showSorterTooltip: false,
        sortOrder: null,
        render: (_, item) => {
          if (!item?.is_registration_on) {
            return null
          }
          return (
            <RegistrationStatusTag
              registration={item.registration}
              isRegistrationOn={item.is_registration_on}
            />
          )
        },
      })
    }

    cols.push({
      // title: 'Options',
      title: '',
      align: 'right',
      key: 'actions',
      width: isMobile ? 50 : 100,
      fixed: isMobile ? false : 'right',
      render: (_, item) => {
        const permission = permissions[item.id] || {}
        let isProfessionalOrUltimateAccount = true
        // Logic commented out in original ScheduleList.jsx, keeping it consistent

        const favoriteState = isFavorites
          ? localFavorites.has(item.id)
          : item.is_favorite

        const moreOptions = []

        if (isProfessionalOrUltimateAccount && permission.update) {
          moreOptions.push({
            key: 'plugin',
            Component: renderGeneratePluginTag,
            componentProps: {
              Button: renderGeneratePluginTag,
              schedule: item,
              scheduleId: item.id,
              onClick: () => {
                onClickWebPluginOnScheduleList(item)
              },
            },
          })
        }

        if (permission.update) {
          moreOptions.push({
            key: 'clone',
            Component: renderCloneCompetitionButton,
            componentProps: {
              onClick: () => onCloneCompetition(item),
              onToggle: () => {},
            },
          })

          moreOptions.push({
            key: 'archive',
            Component: OptionButtonArchive,
            componentProps: {
              name: item.name_full,
              entityType: ENTITY_TYPES.schedule,
              itemId: item.id,
              isArchived: item.is_archive,
              onComplete: () => {
                refresh()
              },
            },
          })
        }

        if (permission.delete) {
          moreOptions.push({
            key: 'delete',
            Component: OptionButtonDelete,
            componentProps: {
              useForceDelete: true,
              unmountOnClose: true,
              entityType: ENTITY_TYPES.schedule,
              itemId: item.id,
              title: 'Delete Competition',
              type: t('common:competition'),
              iconName: 'trash',
              onComplete: () => {
                refresh()
              },
            },
          })
        }

        return (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              justify-content: flex-end;
            `}
          >
            {permission.update && (
              <OptionButtonIcon
                name='pencil'
                onClick={() => {
                  setSyncSalesforce(
                    item?.organization?.sync_salesforce || false
                  )
                  setInitialState(item)
                  setScheduleInitialStateId(item.id)
                  onEditSchedule()
                }}
              />
            )}
            <CustomOptionButtonFavourite
              entityType={ENTITY_TYPES.schedule}
              item={item}
              isFavourite={favoriteState}
              isFavoritesView={isFavorites}
              onFavoriteChange={(newState) =>
                handleFavoriteChange(item.id, newState)
              }
            />
            {moreOptions.length > 0 && <MoreOptions options={moreOptions} />}
          </div>
        )
      },
    })

    return cols
  }, [
    sort,
    direction,
    permissions,
    isFavorites,
    localFavorites,
    onEditSchedule,
    setInitialState,
    onClickWebPluginOnScheduleList,
    handleFavoriteChange,
    sports,
    showParentColumn,
    displayOrganizationColumn,
    parentId,
    items,
    orgFeatures,
    refresh,
    onClickSandboxTour,
    setSyncSalesforce,
    setScheduleInitialStateId,
    specificNameColumn,
    isMobile,
  ])

  return (
    <div
      css={css`
        width: 100%;
      `}
    >
      {/* Global styles for AutoComplete and TreeSelect dropdown */}
      {shouldShowUsersMeFilters && (
        <style>
          {`
            .schedule-list-autocomplete-dropdown {
              z-index: 999999 !important;
              background-color: ${colors.SECONDARY_500} !important;
              padding-top: 4px;
              padding-left: 0;
              padding-right: 0;
              padding-bottom: 0;
            }
            .schedule-list-autocomplete-dropdown .ant-select-item-option-content {
              font-size: 14px !important;
              font-weight: 400 !important;
              color: ${colors.WHITE} !important;
            }
            .schedule-list-autocomplete-dropdown .ant-select-tree {
              background: ${colors.SECONDARY_500} !important;
              color: ${colors.WHITE} !important;
            }
            .schedule-list-autocomplete-dropdown .ant-select-tree-node-content-wrapper {
              color: ${colors.WHITE} !important;
            }
            .schedule-list-autocomplete-dropdown .ant-select-tree-node-content-wrapper:hover {
              background: ${colors.SECONDARY_400} !important;
            }
            .schedule-list-autocomplete-dropdown .ant-select-tree-treenode-selected .ant-select-tree-node-content-wrapper {
              background: ${colors.SECONDARY_400} !important;
            }
            .schedule-list-autocomplete-dropdown .ant-select-tree-switcher {
              color: ${colors.NEUTRAL_50} !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .schedule-list-autocomplete-dropdown .ant-select-tree-indent-unit {
              width: 12px !important;
            }
          `}
        </style>
      )}
      <div
        css={css`
          margin-top: 16px;
          margin-bottom: 16px;
          display: flex;
          justify-content: flex-start;
          flex-wrap: wrap;
          gap: 16px;
          align-items: flex-end;
          @media (max-width: 768px) {
            padding: 0 16px;
            flex-direction: column;
            width: 100%;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
            align-items: center;
            @media (max-width: 768px) {
              width: 100%;
              flex-direction: column;
            }
          `}
        >
          {isMembership && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                align-self: flex-start;
                flex-shrink: 1;
                @media (max-width: 768px) {
                  width: 100%;
                }
              `}
            >
              <MembershipTypeInfoLabel />
              <MembershipTypeFilter
                selected={membershipType}
                onSelect={(value) => {
                  handleMembershipType(value)
                  // Only call legacy callback if new controlled callback is not provided
                  if (!onMembershipTypeChangeExternal) {
                    onMembershipTypeChange?.(value)
                  }
                }}
              />
            </div>
          )}
          <div
            css={css`
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 8px;
              @media (max-width: 768px) {
                width: 100%;
              }
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_0}
            >
              Search
            </Text>
            <Input
              className='list-search-input'
              placeholder={t('common:search')}
              prefix={
                <SearchOutlined
                  css={css`
                    font-size: 16px;
                    color: ${colors.NEUTRAL_50};
                  `}
                />
              }
              suffix={
                loading && items.length > 0 ? <Spin size='small' /> : null
              }
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear={true}
              css={css`
                ${paragraphSmall}
                ${weightRegular}
              width: 300px;
                color: ${colors.WHITE};
                border-radius: 8px;
                background: transparent;
                height: 40px !important;
                .ant-input {
                  background-color: transparent;
                  color: ${colors.WHITE};
                }
                .ant-input-prefix {
                  color: ${colors.NEUTRAL_100};
                }
                &::placeholder {
                  color: ${colors.NEUTRAL_100};
                }
                @media (max-width: 1023px) {
                  width: 100%;
                }
                &:hover {
                  background-color: transparent;
                }
              `}
            />
          </div>
          {shouldShowUsersMeFilters && (
            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 8px;
                @media (max-width: 768px) {
                  width: 100%;
                }
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_0}
              >
                {isCanlan ? 'Leagues' : 'Organizations'}
              </Text>
              <TreeSelect
                css={filterTreeSelect}
                popupClassName='schedule-list-autocomplete-dropdown'
                labelInValue
                value={
                  selectedOrganization
                    ? {
                        value: selectedOrganization.id,
                        label:
                          selectedOrganization.name_full ||
                          selectedOrganization.name,
                      }
                    : {
                        value: ALL_VALUE,
                        label: isCanlan ? 'All Leagues' : 'All Organizations',
                      }
                }
                placeholder={isCanlan ? 'All Leagues' : 'All Organizations'}
                treeData={organizationsTreeData}
                showSearch
                filterTreeNode={(inputValue, treeNode) => {
                  const searchText = treeNode.title?.toLowerCase() || ''
                  return searchText.includes(inputValue.toLowerCase())
                }}
                onChange={(valueObj) => {
                  const value = valueObj?.value
                  if (value === ALL_VALUE) {
                    setSelectedOrganization(null)
                    handleOrganizationIdChange('')
                  } else if (organizationsTree) {
                    const node = findNodeById(organizationsTree, value)
                    if (node) {
                      setSelectedOrganization(treeNodeToOrganization(node))
                      handleOrganizationIdChange(value)
                    }
                  }
                }}
                allowClear={false}
                onClear={() => {
                  setSelectedOrganization(null)
                  handleOrganizationIdChange('')
                }}
                disabled={organizationsLoading}
                treeDefaultExpandedKeys={defaultExpandedKeys}
                treeTitleRender={renderTreeNode}
                popupMatchSelectWidth={isMobile ? true : false}
                dropdownStyle={dropdownScrollStyle}
                notFoundContent={
                  organizationsLoading ? <Spin size='small' /> : null
                }
                open={organizationsDropdownOpen}
                onDropdownVisibleChange={setOrganizationsDropdownOpen}
                suffixIcon={
                  <DownOutlined
                    style={suffixIconStyle}
                    onClick={() =>
                      setOrganizationsDropdownOpen((prev) => !prev)
                    }
                  />
                }
              />
            </div>
          )}

          <div css={filterFieldWrapperStyle}>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_0}
            >
              Sport
            </Text>
            <Select
              placeholder='All Sports'
              value={sportFilter || ALL_VALUE}
              onChange={(value) => {
                if (value === ALL_VALUE) {
                  handleSportFilter(undefined, flatSportOptions)
                } else {
                  handleSportFilter(value, flatSportOptions)
                }
              }}
              options={groupedSportOptions}
              css={filterSelectStyle}
              filterOption={filterOptionGroup}
              showSearch={true}
            />
          </div>

          <div css={filterFieldWrapperStyle}>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_0}
            >
              {t('common:type')}
            </Text>
            <Select
              placeholder='All Types'
              value={competitionTypeFilter || ALL_VALUE}
              onChange={(value) => {
                if (value === ALL_VALUE) {
                  handleCompetitionTypeFilter('')
                } else {
                  handleCompetitionTypeFilter(value)
                }
              }}
              options={COMPETITION_TYPE_FILTER_OPTIONS}
              css={filterSelectStyle}
            />
          </div>

          {/* Show toggle if user has admin permission or orgAdminPermission is true */}
          {/* we should not show the toggle for membership */}
          {((hasAdminPermission && !isMembership) ||
            (orgAdminPermission && !isMembership)) &&
            !alwaysListArchived && (
              <div
                css={css`
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                  max-width: 200px;
                  @media (max-width: 768px) {
                    width: 100%;
                    max-width: none;
                  }
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                  style={`${isMobile ? 'display: none' : ''}`}
                >
                  &nbsp;
                </Text>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px;
                    border-radius: 8px;
                    border: 1px solid ${colors.SECONDARY_200};
                    background: transparent;
                    height: 40px !important;
                    cursor: pointer;
                    @media (max-width: 768px) {
                      width: 100%;
                      justify-content: space-between;
                    }
                  `}
                  onClick={() => handleShowArchived(!showArchived)}
                >
                  <span
                    css={css`
                      color: ${colors.WHITE};
                      font-size: 14px;
                      font-weight: 400;
                    `}
                  >
                    Show Archived
                  </span>
                  <Switch checked={showArchived} size='small' />
                </div>
              </div>
            )}
        </div>
        {showTotalCount && totalCount > 0 && !loading && (
          <div
            css={css`
              display: flex;
              align-items: center;
              height: 40px;
              align-self: flex-end;
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={colors.NEUTRAL_50}
            >
              {totalCount.toLocaleString()}{' '}
              {isCanlan
                ? totalCount === 1
                  ? 'Season'
                  : 'Seasons'
                : totalCount === 1
                ? 'Competition'
                : 'Competitions'}
            </Text>
          </div>
        )}
      </div>
      {error ? (
        <Alert
          message={error}
          type='error'
          showIcon
          css={css`
            margin-bottom: 16px;
            margin-top: 16px;
          `}
        />
      ) : null}
      {loading && items.length === 0 ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
          `}
        >
          <Spin
            size='small'
            css={css`
              .ant-spin-dot-item {
                background-color: ${colors.PRIMARY};
              }
            `}
          />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={items}
          rowKey='id'
          pagination={false}
          onChange={(pagination, filters, sorter) => {
            if (sorter.order) {
              if (
                sorter.field !== specificNameColumn &&
                sorter.order === 'ascend'
              ) {
                handleSort(sorter.field, 'desc')
              } else {
                handleSort(
                  sorter.field,
                  sorter.order === 'ascend' ? 'asc' : 'desc'
                )
              }
            } else {
              handleSort(specificNameColumn, 'asc')
            }
          }}
          size='small'
          scroll={{ x: 'max-content' }}
          css={getTableStyles(isMobile)}
        />
      )}
      {loading && items.length > 0 && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            padding: 20px;
          `}
        >
          <Spin size='small' color={colors.PRIMARY} />
        </div>
      )}
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${colors.NEUTRAL_0} !important;
          }
        `}
      />
    </div>
  )
}

const mapStateToProps = (state) => {
  const genericSports = getAllGenericSportProperties(state)
  return {
    genericSports,
    account: state.account._meAccount,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleListTable)
